import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../supabaseClient";
import { useBag } from "../contexts/BagContext";
import { User, Globe, LayoutGrid, LogOut, Moon, Sun, CircleUser, Search, X, ArrowUpRight, ArrowRight, ChevronDown, ChevronRight, Menu, ChevronLeft, Kanban, ShoppingBag, Bookmark } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const Header = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { items: bagItems, itemCount: bagItemCount } = useBag();
  const navigate = useNavigate();
  const [dropdown, setDropdown] = useState("");
  const [menuVisible, setMenuVisible] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const headerRef = useRef();
  const storeRef = useRef();
  const productsRef = useRef();
  const location = useLocation();
  const [showSearch, setShowSearch] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [menuLeftOffset, setMenuLeftOffset] = useState(0);
  const dropdownTimeout = useRef();
  const scrollRef = useRef(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [theme, setTheme] = useState(() => 
    localStorage.getItem("theme") || "system"
  );

  const checkScroll = useCallback(() => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      setShowScrollIndicator(scrollHeight > clientHeight && scrollTop + clientHeight < scrollHeight - 5);
    }
  }, []);

  useEffect(() => {
    if (dropdown === "search") {
      // Small delay to ensure content is rendered before checking scroll
      const timer = setTimeout(checkScroll, 100);
      return () => clearTimeout(timer);
    }
  }, [dropdown, searchValue, checkScroll]);

  // Animate mega menu open/close & measure position
  useEffect(() => {
    if (dropdown) {
      if (storeRef.current && window.innerWidth > 768) {
        const rect = storeRef.current.getBoundingClientRect();
        setMenuLeftOffset(rect.left);
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setMenuVisible(true));
      });
    } else {
      setMenuVisible(false);
    }
  }, [dropdown]);

  // Close dropdown on route change
  useEffect(() => {
    setDropdown("");
    setMenuVisible(false);
    setIsMobileMenuOpen(false);
    setExpandedAccordion(null);
  }, [location.pathname]);

  // Lock background scroll when dropdown or mobile menu is open
  // Removed scroll lock on dropdown because locking body scroll on hover causes massive layout thrashing and freezing.

  useEffect(() => {
    const applyTheme = () => {
      const themedRoutes = ['/account', '/products/medos', '/shop'];
      const isThemedRoute = themedRoutes.some(route => location.pathname.startsWith(route));

      if (!isThemedRoute) {
        document.documentElement.classList.add("dark");
        return;
      }

      let appliedTheme = theme;
      if (theme === 'system') {
        appliedTheme = window.matchMedia("(prefers-color-scheme: dark)").matches ? 'dark' : 'light';
      }
      
      if (appliedTheme === "dark") {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
    };

    applyTheme();
    localStorage.setItem("theme", theme);

    if (theme === 'system') {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = () => applyTheme();
      mediaQuery.addEventListener("change", handler);
      return () => mediaQuery.removeEventListener("change", handler);
    }

    const handleStorage = (e) => {
      if (e.key === 'theme') {
        setTheme(e.newValue || 'system');
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [theme, location.pathname]);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) {
        setIsProfileLoading(false);
        return;
      }
      
      try {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();
        
        if (profileData) setProfile(profileData);
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally {
        setIsProfileLoading(false);
      }
    };

    fetchProfile();
  }, [user]);

  const siteContent = [
    { title: "Home", description: "FOZZIL homepage", path: "/", category: "Navigate" },
    { title: "Ecosystem", description: "FOZZIL ID & Services", path: process.env.REACT_APP_ECOSYSTEM_URL || "https://ecosystem.fozzil.in", category: "Account" },
    { title: "ZYPHER AI", description: "AI for healthcare", path: "/zypherai", category: "Products" },
    { title: "FOZZIL ARK", description: "Plug-and-play AI Edge-Server", path: "/fozzil-ark", category: "Products" },
    { title: "T.A.E.L.S", description: "Next level AI system", path: "/taels", category: "Products" },
    { title: "AWM", description: "Research: AWM", path: "/awm", category: "Research" },
    { title: "AI Museum", description: "Interactive AI Museum", path: "/research/ai-museum", category: "Research" },
    { title: "THE ARK", description: "The evolution of FOZZIL ARK", path: "/stories/ark", category: "Stories" },
    { title: "The Company", description: "The evolution of FOZZIL", path: "/stories/company", category: "Stories" },
    { title: "Evolution of AI Museum", description: "The story behind Cintel AI Museum", path: "/stories/ai-museum", category: "Stories" },
    { title: "Contact", description: "Contact FOZZIL", path: "https://support.fozzil.in/contact", category: "Company" },
    { title: "About Us", description: "About FOZZIL", path: "/about", category: "Company" },
    { title: "Ethics and Compliance", description: "Ethics and compliance at FOZZIL", path: "/ethics-compliance", category: "Safety" },
    { title: "Terms of Use", description: "FOZZIL Terms of Use", path: "/terms", category: "Legal" },
    { title: "Privacy Policy", description: "FOZZIL Privacy Policy", path: "/privacy", category: "Legal" },
    { title: "Brand Identity", description: "FOZZIL Brand & Design", path: "/brand", category: "Design" },
    { title: "MEDOS", description: "Medical Operating System", path: "/medos", category: "Systems" },
    { title: "Cookie Policy", description: "How we use cookies", path: "/cookie-policy", category: "Legal" },
  ];

  const quickLinks = [
    { title: "T.A.E.L.S", path: "/taels" },
    { title: "FOZZIL Ecosystem", path: process.env.REACT_APP_ECOSYSTEM_URL || "https://ecosystem.fozzil.in" },
    { title: "Terms of Use", path: "/terms" },
    { title: "Safety & Privacy", path: "/security-and-privacy" },
  ];

  const filteredResults = searchValue.trim()
    ? siteContent.filter(
        (item) =>
          item.title.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.description.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.category.toLowerCase().includes(searchValue.toLowerCase())
      )
    : [];

  // Close on click outside the entire header
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setDropdown("");
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        setDropdown("");
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  const openDropdown = useCallback((item) => {
    clearTimeout(dropdownTimeout.current);
    setDropdown(item);
  }, []);

  const scheduleClose = useCallback(() => {
    if (dropdown === "profile" || dropdown === "search" || dropdown === "bag") return;
    dropdownTimeout.current = setTimeout(() => setDropdown(""), 150);
  }, [dropdown]);

  const cancelClose = useCallback(() => {
    clearTimeout(dropdownTimeout.current);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Navigate from mega menu link
  const handleMenuNav = useCallback((e, path) => {
    e.preventDefault();
    e.stopPropagation();

    // Store Subdomain Routing Logic
    if (path.startsWith('/shop')) {
      const currentHost = window.location.hostname.toLowerCase();
      const isStoreDomain = currentHost.includes("store.fozzil.in") || window.location.href.includes("store.fozzil.in");
      
      if (!isStoreDomain) {
        setDropdown("");
        setIsMobileMenuOpen(false);
        const storeUrl = process.env.REACT_APP_STORE_URL || "https://store.fozzil.in";
        const finalUrl = path === '/shop' ? storeUrl : `${storeUrl}${path}`;
        window.location.href = finalUrl;
        return;
      }
    }

    // Terms, Privacy & Security Routing Logic (Always open in a new tab)
    const isTermsPrivacySecurity = [
      '/security-and-privacy',
      '/terms',
      '/privacy',
      '/legal',
      '/ethics-compliance',
      '/cookie-policy',
      '/acceptable-use-policy',
      '/cancellation-and-refund'
    ].includes(path);

    if (isTermsPrivacySecurity) {
      window.open(path, '_blank', 'noopener,noreferrer');
      setDropdown("");
      setIsMobileMenuOpen(false);
      return;
    }

    if (path.startsWith('http')) {
      window.open(path, '_blank');
      setDropdown("");
      setIsMobileMenuOpen(false);
    } else {
      navigate(path);
      setDropdown("");
      setIsMobileMenuOpen(false);
    }
  }, [navigate]);

  const isNavItemActive = (navItem) => {
    switch (navItem) {
      case "home":
        return location.pathname === "/";
      case "products":
        return (
          location.pathname === "/zypherai" ||
          location.pathname === "/taels" ||
          location.pathname === "/fozzil-intelligence" ||
          location.pathname === "/products/medos"
        );
      case "research":
        return (
          location.pathname.startsWith("/research") ||
          location.pathname === "/awm"
        );
      case "pricing":
        return (
          location.pathname === "/pricing" ||
          location.pathname === "/pricing-taels" ||
          location.pathname === "/pricing-intelligence" ||
          location.pathname === "/pricing-medos"
        );
      case "company":
        return (
          location.pathname === "/terms" ||
          location.pathname === "/privacy" ||
          location.pathname === "/ethics-compliance" ||
          location.pathname === "/about" ||
          location.pathname === "/about" ||
          location.pathname === "/brand" ||
          location.pathname === "/legal"
        );
      case "stories":
        return location.pathname.startsWith("/stories");
      default:
        return false;
    }
  };

  const getInitials = (name) => {
    // If profile data is available, prioritize it
    if (profile?.first_name && profile?.last_name) {
      return (profile.first_name[0] + profile.last_name[0]).toUpperCase();
    }
    
    // Fallback logic
    if (!name && user?.email) return user.email[0].toUpperCase();
    if (!name) return "U";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  };

  const linkClass = "text-[13px] text-gray-300 hover:text-white cursor-pointer block py-1.5 transition-colors duration-200";

  return (
    <>
      {/* Backdrop overlay - separate from header so it doesn't interfere */}
      {dropdown && (
        <div 
          className="fixed inset-0 z-[1005]"
          onClick={() => setDropdown("")}
        />
      )}

      <div 
        ref={headerRef}
        className="fixed top-0 left-0 right-0 z-[2000] w-full"
        onMouseLeave={scheduleClose}
      >
        <header className="bg-black/80 backdrop-blur-xl border-b border-white/5 py-0 relative z-[1020]">
          <div className="flex items-center w-full max-w-full px-4 md:px-10 m-0 min-h-[48px] relative">
            <div className="flex-none z-10">
              <a href="https://fozzil.in">
                <img
                  src={process.env.PUBLIC_URL + "/logo.png"}
                  alt="FOZZIL Logo"
                  className="w-[120px] h-auto block md:w-[150px]"
                />
              </a>
            </div>
            {/* Desktop & Tablet Navigation */}
            <div className="hidden lg:flex absolute left-1/2 -translate-x-1/2 items-center h-full">
              <nav className="flex">
                <ul className="flex items-center m-0 p-0">
                  <li 
                    ref={storeRef}
                    className="h-full relative flex items-center"
                    onMouseEnter={() => openDropdown("store")}
                    onMouseLeave={scheduleClose}
                  >
                    <span
                      onClick={() => openDropdown("store")}
                      className={`text-[13px] xl:text-[14px] px-3 xl:px-5 py-3 font-medium tracking-tight cursor-pointer transition-colors duration-200 block
                        ${dropdown === "store" ? "text-white" : "text-gray-400 hover:text-white"}`}
                    >
                      Store
                    </span>
                  </li>
                  <li
                    ref={productsRef}
                    className="h-full relative flex items-center"
                    onMouseEnter={() => openDropdown("products")}
                    onMouseLeave={scheduleClose}
                  >
                    <span 
                      className={`text-[13px] xl:text-[14px] px-3 xl:px-5 py-3 font-medium tracking-tight cursor-pointer transition-colors duration-200 block
                        ${dropdown === "products" ? "text-white" : "text-gray-400 hover:text-white"}`}
                      onClick={() => openDropdown("products")}
                    >
                      Products
                    </span>
                  </li>
                  <li
                    className="h-full relative flex items-center"
                    onMouseEnter={() => openDropdown("research")}
                    onMouseLeave={scheduleClose}
                  >
                    <span 
                      className={`text-[13px] xl:text-[14px] px-3 xl:px-5 py-3 font-medium tracking-tight cursor-pointer transition-colors duration-200 block
                        ${dropdown === "research" ? "text-white" : "text-gray-400 hover:text-white"}`}
                      onClick={() => openDropdown("research")}
                    >
                      Research
                    </span>
                  </li>

                  <li
                    className="h-full relative flex items-center"
                    onMouseEnter={() => openDropdown("stories")}
                    onMouseLeave={scheduleClose}
                  >
                    <span 
                      className={`text-[13px] xl:text-[14px] px-3 xl:px-5 py-3 font-medium tracking-tight cursor-pointer transition-colors duration-200 block
                        ${dropdown === "stories" ? "text-white" : "text-gray-400 hover:text-white"}`}
                      onClick={() => openDropdown("stories")}
                    >
                      Stories
                    </span>
                  </li>

                  <li
                    className="h-full relative flex items-center"
                    onMouseEnter={() => openDropdown("company")}
                    onMouseLeave={scheduleClose}
                  >
                    <span 
                      className={`text-[13px] xl:text-[14px] px-3 xl:px-5 py-3 font-medium tracking-tight cursor-pointer transition-colors duration-200 block
                        ${dropdown === "company" ? "text-white" : "text-gray-400 hover:text-white"}`}
                      onClick={() => openDropdown("company")}
                    >
                      Company
                    </span>
                  </li>

                  <li
                    className="h-full relative flex items-center"
                    onMouseEnter={() => openDropdown("safety")}
                    onMouseLeave={scheduleClose}
                  >
                    <span 
                      className={`text-[13px] xl:text-[14px] px-3 xl:px-5 py-3 font-medium tracking-tight cursor-pointer transition-colors duration-200 block
                        ${dropdown === "safety" ? "text-white" : "text-gray-400 hover:text-white"}`}
                      onClick={() => openDropdown("safety")}
                    >
                      Safety
                    </span>
                  </li>
                </ul>
              </nav>
            </div>

            {/* Icons Section (Mobile & Desktop) */}
            <div className="flex items-center gap-3 sm:gap-6 ml-auto z-10">
              {/* Search Icon */}
              <span
                className="text-white opacity-95 hover:text-blue-400 hover:opacity-100 cursor-pointer flex items-center transition-all p-1.5"
                title="Search"
                onClick={(e) => {
                  e.stopPropagation();
                  if (dropdown === "search") {
                    setDropdown("");
                    setShowSearch(false);
                  } else {
                    setExpandedAccordion(null);
                    openDropdown("search");
                    setShowSearch(true);
                    setIsMobileMenuOpen(false);
                  }
                }}
              >
                {dropdown === "search" ? <X size={20} strokeWidth={1.5} className="w-[18px] md:w-[20px]" /> : <Search size={20} strokeWidth={1.5} className="w-[18px] md:w-[20px]" />}
              </span>

              {/* Bag Icon */}
              <span
                className="relative flex text-white opacity-95 hover:text-blue-400 hover:opacity-100 cursor-pointer items-center transition-all p-1 md:p-1.5"
                title="Shopping Bag"
                onClick={(e) => {
                  e.stopPropagation();
                  if (dropdown === "bag") {
                    setDropdown("");
                  } else {
                    setExpandedAccordion(null);
                    openDropdown("bag");
                    setShowSearch(false);
                    setIsMobileMenuOpen(false);
                  }
                }}
              >
                {dropdown === "bag" ? <X size={20} strokeWidth={1.5} className="w-[18px] md:w-[20px]" /> : <ShoppingBag size={20} strokeWidth={1.5} className="w-[18px] md:w-[20px]" />}
                {bagItemCount > 0 && dropdown !== "bag" && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#065DC0] text-white text-[9px] font-bold flex items-center justify-center leading-none">{bagItemCount}</span>
                )}
              </span>

              {/* Profile Icon */}
              {isAuthenticated ? (
                <div className="relative">
                  <button
                    className="flex items-center justify-center w-7 h-7 md:w-8 md:h-8 rounded-full border border-blue-500/30 bg-zinc-900/50 text-white text-[10px] md:text-[11px] font-bold hover:bg-zinc-800 hover:border-blue-500/50 transition-all duration-300 overflow-hidden"
                    onClick={() => {
                      if (dropdown === "profile") {
                        cancelClose();
                        setDropdown("");
                      } else {
                        cancelClose();
                        openDropdown("profile");
                        setIsMobileMenuOpen(false);
                        setExpandedAccordion(null);
                      }
                    }}
                  >
                    {isProfileLoading ? (
                      <div className="w-full h-full bg-zinc-800 animate-pulse rounded-full" />
                    ) : (profile?.avatar_url || user.user_metadata?.avatar_url) ? (
                      <img 
                        src={profile?.avatar_url || user.user_metadata.avatar_url} 
                        alt="Profile" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      getInitials(user.user_metadata?.full_name)
                    )}
                  </button>
                  
                  {/* Desktop Profile Dropdown */}
                  <div className={`hidden md:block absolute right-0 top-full mt-2 w-64 rounded-[32px] border border-white/5 shadow-[0_16px_48px_rgba(0,0,0,0.5)] py-4 transition-all duration-300 transform-gpu origin-top-right backdrop-blur-xl bg-black/90 text-white z-[1010] ${
                    dropdown === "profile" 
                      ? "visible opacity-100 scale-100" 
                      : "invisible opacity-0 scale-95 pointer-events-none"
                  }`}>
                    <div className="px-6 py-3 border-b border-white/5 mb-3">
                      <p className="text-sm font-bold truncate text-white uppercase tracking-tight">
                        {profile?.first_name ? `${profile.first_name} ${profile.last_name}` : (user.user_metadata?.full_name || user.email?.split('@')[0] || 'User')}
                      </p>
                      <p className="text-[11px] truncate text-gray-500 tracking-tighter mt-0.5">
                        {user.email}
                      </p>
                    </div>

                    <div className="space-y-1">
                      <button 
                        onClick={() => window.open(process.env.REACT_APP_ECOSYSTEM_URL || 'https://ecosystem.fozzil.in', '_blank')}
                        className="mx-2 w-[calc(100%-16px)] text-left px-5 py-2.5 flex items-center gap-4 transition-all rounded-[32px] hover:bg-white/5 text-gray-200 hover:text-blue-400 group/item"
                      >
                        <div className="w-[18px] h-[18px] flex items-center justify-center">
                          <img 
                            src="/Ecosystem Logo without bg.png" 
                            alt="Ecosystem" 
                            className="w-full h-full object-contain filter group-hover/item:brightness-125 transition-all" 
                          />
                        </div>
                        <span className="text-sm font-normal tracking-tight">Ecosystem</span>
                      </button>

                      <button 
                        onClick={() => {
                          window.open("https://account.fozzil.in", "_blank");
                          setDropdown("");
                        }}
                        className="mx-2 w-[calc(100%-16px)] text-left px-5 py-2.5 flex items-center gap-4 transition-all rounded-[32px] hover:bg-white/5 text-gray-200 hover:text-blue-400 group/item"
                      >
                        <User size={18} className="text-blue-400/80 group-hover/item:text-blue-400 transition-colors" /> 
                        <span className="text-sm font-normal tracking-tight">Manage Account</span>
                      </button>
                    </div>

                    <div className="px-6 mt-4 flex justify-center">
                      <button 
                          onClick={async () => {
                          try {
                            await logout();
                            navigate('/'); 
                          } catch (error) {
                            console.error("Logout failed", error);
                          }
                        }}
                        className="w-1/2 bg-[#065DC0] text-white py-2 rounded-[32px] text-[11px] font-bold tracking-wider hover:bg-[#0077ed] transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-900/20"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>

                  {/* Mobile Profile Dropdown - persistent DOM for zero mount lag */}
                  <div 
                    className={`md:hidden fixed left-0 right-0 top-[48px] bg-black/95 backdrop-blur-2xl border-b border-white/10 py-6 px-6 z-[1010] rounded-b-[32px] transform-gpu transition-all duration-200 ease-out ${
                      dropdown === "profile" ? "opacity-100 translate-y-0 visible pointer-events-auto" : "opacity-0 -translate-y-4 invisible pointer-events-none"
                    }`}
                  >
                       <div className="mb-6 pb-6 border-b border-white/10">
                          <p className="text-lg font-semibold text-white">
                            {profile?.first_name ? `${profile.first_name} ${profile.last_name}` : (user.user_metadata?.full_name || user.email?.split('@')[0] || 'User')}
                          </p>
                          <p className="text-sm text-gray-400">
                            {user.email}
                          </p>
                        </div>

                        <div className="flex flex-col gap-4">
                          <button 
                            onClick={() => { window.open(process.env.REACT_APP_ECOSYSTEM_URL || 'https://ecosystem.fozzil.in', '_blank'); setDropdown(""); }}
                            className="flex items-center gap-4 text-gray-200 hover:text-blue-400 transition-colors"
                          >
                            <div className="w-[20px] h-[20px] flex items-center justify-center">
                              <img 
                                src="/Ecosystem Logo without bg.png" 
                                alt="Ecosystem" 
                                className="w-full h-full object-contain" 
                              />
                            </div>
                            <span className="text-base font-medium">Ecosystem</span>
                          </button>   
                          
                          <button 
                            onClick={() => {
                              window.open("https://account.fozzil.in", "_blank");
                              setDropdown("");
                            }}
                            className="flex items-center gap-4 text-gray-200 hover:text-blue-400 transition-colors"
                          >
                            <User size={20} className="text-blue-400" /> 
                            <span className="text-base font-medium">Manage Account</span>
                          </button>               

                          

                          <button 
                              onClick={async () => {
                              try {
                                await logout();
                                navigate('/'); 
                                setDropdown("");
                              } catch (error) {
                                console.error("Logout failed", error);
                              }
                            }}
                            className="w-fit px-8 mx-auto block mt-4 bg-[#065DC0] text-white py-1.5 rounded-full text-xs font-semibold hover:bg-[#0077ed] transition-all"
                          >
                            Sign Out
                          </button>
                        </div>
                    </div>
                </div>
              ) : (
                <button
                  className="hidden lg:flex px-3 py-1.5 md:px-5 text-[11px] font-bold text-white bg-transparent border border-blue-500/40 rounded-full hover:bg-blue-500/20 hover:border-blue-500/60 transition-all duration-300 items-center gap-1.5 md:gap-2 group"
                  onClick={() => navigate('/auth', { state: { from: { pathname: location.pathname } } })}
                >
                  <span className="hidden sm:inline">Sign In</span>
                  <span className="sm:hidden">Sign In</span>
                  <ArrowUpRight size={16} strokeWidth={1.5} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
                </button>
              )}

              {/* Mobile Menu Icon */}
              <button
                className="lg:hidden text-white p-1.5 focus:outline-none touch-manipulation"
                onClick={() => {
                  setIsMobileMenuOpen(!isMobileMenuOpen);
                  setDropdown("");
                  setExpandedAccordion(null);
                }}
              >
                {isMobileMenuOpen ? <X size={24} className="w-[20px] md:w-[24px]" /> : <Kanban size={24} className="rotate-180 w-[20px] md:w-[24px]" />}
              </button>
            </div>
          </div>

        </header>

        {/* New Mobile Mega Menu - Moved outside header for proper stacking context */}
        {/* New Mobile Mega Menu - Moved outside header for proper stacking context */}
        {/* Backdrop */}
        <div 
          onClick={() => setIsMobileMenuOpen(false)}
          className={`md:hidden fixed inset-0 top-[48px] bg-black/70 z-[1008] transition-opacity duration-200 ease-out ${isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        />

        {/* Mega Menu Container - Always rendered, CSS animated */}
        <div 
          className={`lg:hidden fixed top-[48px] left-0 right-0 w-full h-[calc(100vh-48px)] bg-black/[0.97] backdrop-blur-md z-[1010] overflow-hidden border-b border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.5)] transition-all duration-300 ease-out ${isMobileMenuOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-4 pointer-events-none'}`}
        >
          <div className="relative w-full h-full overflow-y-auto overflow-x-hidden no-scrollbar pb-6">
            <AnimatePresence mode="wait">
              {!expandedAccordion ? (
                <motion.div
                  key="main-menu"
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.2 }}
                  className="px-6 py-4 flex flex-col w-full min-h-full"
                >
                  <div className="flex flex-col text-left gap-1 mt-4">
                    {['store', 'products', 'research', 'stories', 'company', 'safety'].map((category) => (
                      <button 
                        key={category}
                        onClick={() => setExpandedAccordion(category)}
                        className="w-full py-5 flex items-center justify-between text-2xl font-medium text-gray-200 group touch-manipulation border-b border-white/5 last:border-0"
                      >
                        <span className="group-hover:text-blue-400 transition-colors capitalize tracking-tight">{category}</span>
                        <ChevronRight className="text-gray-500 group-hover:text-blue-400 transition-colors" />
                      </button>
                    ))}
                  </div>

                  {!isAuthenticated && (
                    <div className="mt-8 mb-4 flex justify-center">
                      <button
                        className="w-fit px-6 py-2 text-xs font-bold text-white bg-transparent border border-blue-500/40 rounded-full hover:bg-blue-500/20 hover:border-blue-500/60 transition-all duration-300 flex items-center justify-center gap-1.5 group"
                        onClick={() => navigate('/auth', { state: { from: { pathname: location.pathname } } })}
                      >
                        <span>Sign In</span>
                        <ArrowUpRight size={14} strokeWidth={1.5} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
                      </button>
                    </div>
                  )}
                </motion.div>
              ) : (
                <motion.div
                  key="sub-menu"
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 50 }}
                  transition={{ duration: 0.2 }}
                  className="px-6 py-4 flex flex-col w-full min-h-full"
                >
                  <button 
                    onClick={() => setExpandedAccordion(null)}
                    className="flex items-center gap-2 text-gray-400 hover:text-white mb-8 group touch-manipulation w-fit"
                  >
                    <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                    <span className="text-xs font-semibold tracking-wider capitalize">Back to Menu</span>
                  </button>
                  
                  <h2 className="text-3xl font-medium text-white uppercase tracking-tight mb-8 border-b border-white/10 pb-4">{expandedAccordion}</h2>

                  <div className="flex flex-col gap-8 w-full">
                    {expandedAccordion === 'store' && (
                      <div className="flex flex-col gap-4">
                        <h3 className="text-[11px] font-bold text-gray-500 uppercase tracking-wider pb-2 border-b border-white/5">Shop</h3>
                        <div className="flex flex-col gap-5">
                          <a href="/shop" onClick={(e) => handleMenuNav(e, '/shop')} className="text-xl font-medium text-gray-300 hover:text-blue-400 uppercase transition-colors">Ark</a>
                          <a href="/shop" onClick={(e) => handleMenuNav(e, '/shop/buy-ark/ark-pro')} className="text-xl font-medium text-gray-300 hover:text-blue-400 uppercase transition-colors">Ark Pro</a>
                          <a href="/shop" onClick={(e) => handleMenuNav(e, '/shop/medos')} className="text-xl font-medium text-gray-300 hover:text-blue-400 uppercase transition-colors">MedOS</a>
                          <a href="/shop" onClick={(e) => handleMenuNav(e, '/shop/intelligence')} className="text-xl font-medium text-gray-300 hover:text-blue-400 uppercase transition-colors">T.A.E.L.S</a>
                        </div>
                      </div>
                    )}

                    {expandedAccordion === 'products' && (
                      <>
                        <div className="flex flex-col gap-4">
                          <h3 className="text-[11px] font-bold text-gray-500 capitalize tracking-wider pb-2 border-b border-white/5">Core Solutions</h3>
                          <div className="flex flex-col gap-5">
                             <a href="/fozzil-ark" onClick={(e) => { e.preventDefault(); navigate('/fozzil-ark'); setIsMobileMenuOpen(false); }} className="text-xl font-medium text-gray-300 hover:text-blue-400 transition-colors">Ark</a>
                             <a href="/taels" onClick={(e) => { e.preventDefault(); navigate('/taels'); setIsMobileMenuOpen(false); }} className="text-xl font-medium text-gray-300 hover:text-blue-400 transition-colors">T.A.E.L.S</a>
                             <a href="/medos" onClick={(e) => { e.preventDefault(); navigate('/medos'); setIsMobileMenuOpen(false); }} className="text-xl font-medium text-gray-300 hover:text-blue-400 transition-colors">MedOS</a>
                          </div>
                        </div>
                        <div className="flex flex-col gap-4">
                          <h3 className="text-[11px] font-bold text-gray-500 capitalize tracking-wider pb-2 border-b border-white/5">Medical Systems</h3>
                          <div className="flex flex-col gap-5">
                             <a href="/zypherai" onClick={(e) => { e.preventDefault(); navigate('/zypherai'); setIsMobileMenuOpen(false); }} className="text-xl font-medium text-gray-300 hover:text-blue-400 transition-colors">Zypher AI</a>
                          </div>
                        </div>
                      </>
                    )}

                    {expandedAccordion === 'research' && (
                      <div className="flex flex-col gap-4">
                        <h3 className="text-[11px] font-bold text-gray-500 capitalize tracking-wider pb-2 border-b border-white/5">Frontier Labs</h3>
                        <div className="flex flex-col gap-5">
                           <a href="/research/ai-museum" onClick={(e) => { e.preventDefault(); navigate('/research/ai-museum'); setIsMobileMenuOpen(false); }} className="text-xl font-medium text-gray-300 hover:text-blue-400 transition-colors">AI Museum</a>
                           <a href="/research/awm" onClick={(e) => { e.preventDefault(); navigate('/research/awm'); setIsMobileMenuOpen(false); }} className="text-xl font-medium text-gray-300 hover:text-blue-400 transition-colors">AWM</a>
                        </div>
                      </div>
                    )}

                    {expandedAccordion === 'stories' && (
                      <div className="flex flex-col gap-4">
                        <h3 className="text-[11px] font-bold text-gray-500 capitalize tracking-wider pb-2 border-b border-white/5">Our Journey</h3>
                        <div className="flex flex-col gap-5">
                          <a href="/stories/ark" onClick={(e) => { e.preventDefault(); navigate('/stories/ark'); setIsMobileMenuOpen(false); }} className="text-xl font-medium text-gray-300 hover:text-blue-400 transition-colors">The Ark</a>
                          <a href="/stories/company" onClick={(e) => { e.preventDefault(); navigate('/stories/company'); setIsMobileMenuOpen(false); }} className="text-xl font-medium text-gray-300 hover:text-blue-400 transition-colors">The Company</a>
                          <a href="/stories/ai-museum" onClick={(e) => { e.preventDefault(); navigate('/stories/ai-museum'); setIsMobileMenuOpen(false); }} className="text-xl font-medium text-gray-300 hover:text-blue-400 transition-colors">Cintel AI Museum</a>
                        </div>
                      </div>
                    )}

                    {expandedAccordion === 'company' && (
                      <div className="flex flex-col gap-5 pt-2">
                        <a href="/about" onClick={(e) => { e.preventDefault(); navigate('/about'); setIsMobileMenuOpen(false); }} className="text-xl font-medium text-gray-300 hover:text-blue-400 transition-colors">About FOZZIL</a>
                        <a href="https://support.fozzil.in/contact" className="text-xl font-medium text-gray-300 hover:text-blue-400 transition-colors">Contact</a>
                        <a href="/brand" onClick={(e) => { e.preventDefault(); navigate('/brand'); setIsMobileMenuOpen(false); }} className="text-xl font-medium text-gray-300 hover:text-blue-400 transition-colors">Brand Identity</a>
                        <a href="/ethics-compliance" target="_blank" rel="noopener noreferrer" onClick={() => setIsMobileMenuOpen(false)} className="text-xl font-medium text-gray-300 hover:text-blue-400 transition-colors">Ethics & Compliance</a>
                        <a href="/jobs" onClick={(e) => { e.preventDefault(); navigate('/jobs'); setIsMobileMenuOpen(false); }} className="text-xl font-medium text-gray-300 hover:text-blue-400 transition-colors">Careers & Jobs</a>
                      </div>
                    )}

                    {expandedAccordion === 'safety' && (
                      <div className="flex flex-col gap-5 pt-2">
                        <a href="/security-and-privacy" target="_blank" rel="noopener noreferrer" onClick={() => setIsMobileMenuOpen(false)} className="text-xl font-medium text-gray-300 hover:text-blue-400 transition-colors">Security and Privacy</a>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Mega Menu Panel - Persistent DOM element to avoid mount freezing */}
        <div
          className={`fixed md:absolute md:right-0 md:top-full top-[48px] left-0 right-0 md:left-auto z-[1010] bg-black/95 md:bg-black/90 backdrop-blur-2xl md:backdrop-blur-xl border-b md:border-l border-white/10 md:border-white/5 shadow-[0_20px_50px_rgba(0,0,0,0.5)] md:shadow-[0_16px_48px_rgba(0,0,0,0.5)] rounded-b-[32px] transform-gpu transition-all duration-300 ease-out ${
            dropdown && dropdown !== "profile" ? "opacity-100 translate-y-0 pointer-events-auto visible" : "opacity-0 -translate-y-4 pointer-events-none invisible"
          }`}
          style={window.innerWidth > 768 ? { left: `${menuLeftOffset}px` } : {}}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
            <div className={`transition-opacity duration-200 ${dropdown === "store" ? "opacity-100 block" : "opacity-0 hidden"}`}>
              <div className="px-10 py-8 grid grid-cols-3 gap-16 text-left">
                  <div className="flex flex-col gap-4">
                    <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em]">Shop</h3>
                    <ul className="flex flex-col gap-2 m-0 p-0">
                      <li><a href="/shop" onClick={(e) => handleMenuNav(e, '/shop')} className={linkClass}>Ark</a></li>
                      <li><a href="/shop/buy-ark/ark-pro" onClick={(e) => handleMenuNav(e, '/shop/buy-ark/ark-pro')} className={linkClass}>Ark Pro</a></li>
                      <li><a href="/shop/medos" onClick={(e) => handleMenuNav(e, '/shop/medos')} className={linkClass}>MedOS</a></li>
                      <li><a href="/shop/intelligence" onClick={(e) => handleMenuNav(e, '/shop/intelligence')} className={linkClass}>T.A.E.L.S</a></li>
                    </ul>
                  </div>
                  <div className="flex flex-col gap-4">
                    <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em]">Quick Links</h3>
                    <ul className="flex flex-col gap-2 m-0 p-0">
                      <li><a href="https://store.fozzil.in" className={linkClass}>View Store</a></li>
                      <li><a href="https://store.fozzil.in/shop/saves" className={linkClass}>Saved Items</a></li>
                      <li><a href="https://store.fozzil.in/shop/orders" className={linkClass}>Orders</a></li>
                    </ul>
                  </div>
                </div>
            </div>

            <div className={`transition-opacity duration-200 ${dropdown === "products" ? "opacity-100 block" : "opacity-0 hidden"}`}>
              <div className="px-10 py-8 grid grid-cols-3 gap-16 text-left">
                  <div className="flex flex-col gap-4">
                    <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em]">Core Solutions</h3>
                    <ul className="flex flex-col gap-2 m-0 p-0">
                      <li><a href="/fozzil-ark" onClick={(e) => handleMenuNav(e, '/fozzil-ark')} className={linkClass}>ARK</a></li>
                      <li><a href="/taels" onClick={(e) => handleMenuNav(e, '/taels')} className={linkClass}>T.A.E.L.S</a></li>
                      <li><a href="/medos" onClick={(e) => handleMenuNav(e, '/medos')} className={linkClass}>MEDOS</a></li>
                    </ul>
                  </div>
                  <div className="flex flex-col gap-4">
                    <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em]">Medical Systems</h3>
                    <ul className="flex flex-col gap-2 m-0 p-0">
                      <li><a href="/zypherai" onClick={(e) => handleMenuNav(e, '/zypherai')} className={linkClass}>ZYPHER AI</a></li>
                    </ul>
                  </div>
                  <div className="flex flex-col gap-4">
                    <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em]">Quick Links</h3>
                    <ul className="flex flex-col gap-2 m-0 p-0">
                      <li><a href={process.env.REACT_APP_ECOSYSTEM_URL || "https://ecosystem.fozzil.in"} target="_blank" rel="noopener noreferrer" onClick={(e) => handleMenuNav(e, process.env.REACT_APP_ECOSYSTEM_URL || 'https://ecosystem.fozzil.in')} className={linkClass}>FOZZIL Ecosystem</a></li>
                      <li><a href="/brand" onClick={(e) => handleMenuNav(e, '/brand')} className={linkClass}>Brand Identity</a></li>
                    </ul>
                  </div>
                </div>
            </div>

            <div className={`transition-opacity duration-200 ${dropdown === "research" ? "opacity-100 block" : "opacity-0 hidden"}`}>
              <div className="px-10 py-8 grid grid-cols-3 gap-16 text-left">
                  <div className="flex flex-col gap-4">
                    <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em]">Frontier Labs</h3>
                    <ul className="flex flex-col gap-2 m-0 p-0">
                      <li><a href="/research/ai-museum" onClick={(e) => handleMenuNav(e, '/research/ai-museum')} className={linkClass}>AI MUSEUM</a></li>
                      <li><a href="/research/awm" onClick={(e) => handleMenuNav(e, '/research/awm')} className={linkClass}>AWM</a></li>
                    </ul>
                  </div>
                </div>
            </div>

            <div className={`transition-opacity duration-200 ${dropdown === "stories" ? "opacity-100 block" : "opacity-0 hidden"}`}>
              <div className="px-10 py-8 grid grid-cols-3 gap-16 text-left">
                  <div className="flex flex-col gap-4">
                    <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em]">Our Journey</h3>
                    <ul className="flex flex-col gap-2 m-0 p-0">
                      <li><a href="/stories/ark" onClick={(e) => handleMenuNav(e, '/stories/ark')} className={linkClass}>THE ARK</a></li>
                      <li><a href="/stories/ai-museum" onClick={(e) => handleMenuNav(e, '/stories/ai-museum')} className={linkClass}>CINTEL AI MUSEUM</a></li>
                      <li><a href="/stories/company" onClick={(e) => handleMenuNav(e, '/stories/company')} className={linkClass}>THE COMPANY</a></li>
                    </ul>
                  </div>
                </div>
            </div>

            <div className={`transition-opacity duration-200 ${dropdown === "company" ? "opacity-100 block" : "opacity-0 hidden"}`}>
              <div className="px-10 py-8 grid grid-cols-3 gap-16 text-left">
                  <div className="flex flex-col gap-4">
                    <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em]">Company</h3>
                    <ul className="flex flex-col gap-2 m-0 p-0">
                      <li><a href="/about" onClick={(e) => handleMenuNav(e, '/about')} className={linkClass}>About</a></li>
                      <li><a href="https://support.fozzil.in/contact" className={linkClass}>Contact Us</a></li>
                      <li><a href="/brand" onClick={(e) => handleMenuNav(e, '/brand')} className={linkClass}>Brand</a></li>
                      <li><a href="/ethics-compliance" target="_blank" rel="noopener noreferrer" onClick={(e) => handleMenuNav(e, '/ethics-compliance')} className={linkClass}>Ethics & Compliance</a></li>
                      <li><a href="/jobs" onClick={(e) => handleMenuNav(e, '/jobs')} className={linkClass}>Careers & Jobs</a></li>
                    </ul>
                  </div>
                </div>
            </div>

            <div className={`transition-opacity duration-200 ${dropdown === "safety" ? "opacity-100 block" : "opacity-0 hidden"}`}>
              <div className="px-10 py-8 grid grid-cols-3 gap-16 text-left">
                  <div className="flex flex-col gap-4">
                    <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em]">Safety</h3>
                    <ul className="flex flex-col gap-2 m-0 p-0">
                      <li><a href="/security-and-privacy" target="_blank" rel="noopener noreferrer" onClick={(e) => handleMenuNav(e, '/security-and-privacy')} className={linkClass}>Security and Privacy</a></li>
                    </ul>
                  </div>
                </div>
            </div>

            <div className={`transition-opacity duration-200 ${dropdown === "search" ? "opacity-100 block" : "opacity-0 hidden"}`}>
              <div className="px-6 md:px-10 py-6 min-h-[200px] text-left">
                  {/* Internal Search Bar */}
                  <div className="flex items-center gap-4 mb-6 pb-2 border-b border-white/5">
                    <Search size={20} className="text-gray-500" />
                    <input
                      className="w-full bg-transparent text-xl font-medium text-white placeholder-gray-600 focus:outline-none"
                      type="text"
                      autoFocus
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      placeholder="Search FOZZIL"
                    />
                  </div>

                  <div 
                    ref={scrollRef}
                    onScroll={checkScroll}
                    className="max-h-[40vh] overflow-y-auto pr-4 custom-scrollbar relative"
                  >
                    {searchValue.trim() ? (
                      <div className="flex flex-col gap-6">
                        <div>
                          <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-4">Suggested Links</h3>
                          {filteredResults.length > 0 ? (
                            <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                              {filteredResults.map((result, idx) => (
                                <button
                                  key={idx}
                                  onClick={(e) => handleMenuNav(e, result.path)}
                                  className="group flex items-center justify-between py-2 border-b border-white/5 hover:border-blue-500/30 transition-all text-left"
                                >
                                  <div className="flex flex-col">
                                    <span className="text-sm font-medium text-white group-hover:text-blue-400 transition-colors uppercase tracking-tight">{result.title}</span>
                                    <span className="text-[10px] text-gray-500 uppercase tracking-tighter mt-0.5">{result.category}</span>
                                  </div>
                                  <ArrowRight size={14} className="text-gray-600 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
                                </button>
                              ))}
                            </div>
                          ) : (
                            <div className="py-10 text-center">
                               <p className="text-base text-gray-500">No results found for "{searchValue}"</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-6">
                        <div>
                          <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-4">Quick Links</h3>
                          <ul className="flex flex-col gap-3 m-0 p-0">
                            {quickLinks.map((link, idx) => (
                              <li key={idx}>
                                <button
                                  onClick={(e) => handleMenuNav(e, link.path)}
                                  className="group flex items-center gap-3 text-sm font-medium text-gray-300 hover:text-blue-400 transition-colors"
                                >
                                  <ArrowRight size={14} className="text-gray-600 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
                                  {link.title}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Scroll Indicator Arrow */}
                  {showScrollIndicator && (
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 pointer-events-none animate-bounce">
                      <div className="bg-zinc-800/80 backdrop-blur rounded-full p-1 border border-white/10">
                        <ChevronDown size={14} className="text-blue-400" />
                      </div>
                    </div>
                  )}
                </div>
            </div>

            <div className={`transition-opacity duration-200 ${dropdown === "bag" ? "opacity-100 block" : "opacity-0 hidden"}`}>
              <div className="px-6 md:px-10 py-6 md:py-8 text-left">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-white">Bag</h2>
                  {bagItemCount > 0 && (
                    <button
                      onClick={(e) => handleMenuNav(e, '/shop/bag')}
                      className="px-6 py-2.5 rounded-full bg-[#065DC0] text-white text-sm font-semibold border-none cursor-pointer hover:bg-[#0077ed] transition-all"
                    >
                      Review Bag
                    </button>
                  )}
                </div>

                {bagItemCount === 0 ? (
                  <div className="py-8 text-center">
                    <ShoppingBag size={36} className="text-gray-600 mx-auto mb-3" />
                    <p className="text-sm font-semibold text-gray-300">Your bag is empty.</p>
                    <p className="text-xs text-gray-500 mt-1">Add items to get started.</p>
                  </div>
                ) : (
                  <div className="space-y-3 mb-8 max-h-[30vh] overflow-y-auto pr-2">
                    {bagItems.map((item) => (
                      <div key={item.bagId} className="flex items-center gap-4 py-3 border-b border-white/5">
                        <img src={item.image} alt={item.name} className="w-14 h-14 object-contain rounded-xl bg-white/5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate">{item.name}</p>
                          {item.config && item.config.slice(0, 2).map(({label, value}) => (
                            <p key={label} className="text-xs text-gray-500 truncate">{value}</p>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {isAuthenticated && (
                  <div className="border-t border-white/5 pt-6">
                    <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em] mb-4">My Profile</h3>
                    <ul className="flex flex-col gap-2 m-0 p-0">
                      <li><button onClick={(e) => handleMenuNav(e, '/shop/orders')} className="text-sm font-semibold text-gray-300 hover:text-white transition-colors bg-transparent border-none cursor-pointer p-0">Orders</button></li>
                      <li><button onClick={(e) => handleMenuNav(e, '/shop/saves')} className="text-sm font-semibold text-gray-300 hover:text-white transition-colors bg-transparent border-none cursor-pointer p-0">Saved Items</button></li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
        </div>
      </div>
    </>
  );
};


export default Header;
